import { useState } from 'react';

import { Search } from 'lucide-react';
import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

import { CrmConnections, PlanOkCrmEntry, SciCrmEntry, SciMappingRow } from '../types/integrations';

interface Props {
  data: CrmConnections;
}

const CRM_LABELS: Record<string, string> = {
  crm: 'CRM Principal',
  salesforce: 'Salesforce',
  pipeDrive: 'PipeDrive',
  planOk: 'PlanOK',
  mobysuite: 'Mobysuite',
  hubspot: 'HubSpot',
  clientify: 'Clientify',
  sci: 'SCI',
  dHunter: 'dHunter',
  iconcreta: 'iConcreta',
};

const NotInDb = ({ id }: { id: number }) => (
  <span className="flex items-center gap-1.5 font-mono text-text-subtle">
    <span className="inline-block size-2 rounded-full bg-error flex-shrink-0" />
    ID {id}
  </span>
);

const matchesQuery = (query: string, ...fields: (string | number | null | undefined)[]): boolean => {
  const q = query.toLowerCase();
  return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
};

function sciMappingMatchesSearch(query: string, m: SciMappingRow): boolean {
  return matchesQuery(query, m.subProjectName, m.subProjectId, m.sciInputName, m.sciOutputIdentifier);
}

const SciMappingTable = ({ rows }: { rows: SciMappingRow[] }) => (
  <table className="w-full text-xs mt-2 border-collapse">
    <thead>
      <tr className="border-b border-border-subtle">
        <th className="text-left py-1 pr-4 font-medium text-text-subtle">Entrada SCI</th>
        <th className="text-left py-1 pr-4 font-medium text-text-subtle">Nombre Lidz</th>
        <th className="text-left py-1 font-medium text-text-subtle">Salida SCI</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.subProjectId} className="border-b border-border-subtle last:border-0">
          <td className="py-1 pr-4 text-text-subtle font-mono">{row.sciInputName ?? '—'}</td>
          <td className="py-1 pr-4">
            {row.isInDb ? (
              <span className="text-text-default">{row.subProjectName}</span>
            ) : (
              <NotInDb id={row.subProjectId} />
            )}
          </td>
          <td className="py-1 text-text-subtle font-mono">{row.sciOutputIdentifier ?? '—'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PlanOkDetails = ({ entry }: { entry: PlanOkCrmEntry }) => {
  const [query, setQuery] = useState('');

  if (!entry.mappings.length) return null;

  const filtered = query
    ? entry.mappings.filter((m) => matchesQuery(query, m.subProjectName, m.planOkId, m.subProjectId))
    : entry.mappings;

  return (
    <div className="mt-3 flex flex-col gap-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-text-subtle pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre o ID…"
          className="h-7 pl-8 text-xs"
        />
      </div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-border-subtle">
            <th className="text-left py-1 pr-4 font-medium text-text-subtle">Subproyecto</th>
            <th className="text-left py-1 font-medium text-text-subtle">PlanOk ID</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={2} className="py-2 text-text-subtle text-center">
                Sin resultados
              </td>
            </tr>
          ) : (
            filtered.map(({ subProjectId, subProjectName, planOkId, isInDb }) => (
              <tr key={subProjectId} className="border-b border-border-subtle last:border-0">
                <td className="py-1 pr-4">
                  {isInDb ? <span className="text-text-default">{subProjectName}</span> : <NotInDb id={subProjectId} />}
                </td>
                <td className="py-1 text-text-subtle font-mono">{planOkId}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const SciDetails = ({ entry }: { entry: SciCrmEntry }) => {
  const [query, setQuery] = useState('');

  if (!entry.mappings.length) return null;

  const filtered = query ? entry.mappings.filter((m) => sciMappingMatchesSearch(query, m)) : entry.mappings;

  return (
    <div className="mt-3 flex flex-col gap-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-text-subtle pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre o ID…"
          className="h-7 pl-8 text-xs"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-xs text-text-subtle text-center py-2">Sin resultados</p>
      ) : (
        <SciMappingTable rows={filtered} />
      )}
    </div>
  );
};

const CrmConnectionsSection = ({ data }: Props) => {
  const sectionProps = useCollapsibleSection('crm-connections');
  type CrmEntry = CrmConnections[keyof CrmConnections];
  const allEntries = Object.entries(data) as [keyof CrmConnections, CrmEntry][];
  const connectedEntries = allEntries.filter(([, v]) => v.connected);

  return (
    <Accordion
      type="single"
      collapsible
      {...sectionProps}
      className="bg-bg-surface border border-border-subtle rounded-xl"
    >
      <AccordionItem value="content" className="border-none">
        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
          <span className="flex items-center gap-3">
            <span className="font-semibold text-text-default">Conexiones CRM</span>
            <Badge
              className={
                connectedEntries.length > 0 ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'
              }
            >
              {connectedEntries.length > 0
                ? `${connectedEntries.length} conectado${connectedEntries.length > 1 ? 's' : ''}`
                : 'Sin conexiones'}
            </Badge>
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
          <div className="pt-4">
            {connectedEntries.length === 0 ? (
              <p className="text-sm text-text-muted">No hay integraciones CRM configuradas para este proyecto.</p>
            ) : (
              <ul className="flex flex-col">
                {connectedEntries.map(([key, value], index) => (
                  <li key={key}>
                    {index > 0 && <Separator className="my-2" />}
                    <div className="flex items-center justify-between py-1">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-text-default">{CRM_LABELS[key] ?? key}</span>
                        {key === 'crm' && value.type && (
                          <span className="text-xs text-text-subtle capitalize">{value.type}</span>
                        )}
                      </div>
                      <Badge className="bg-success-light text-success">Conectado</Badge>
                    </div>
                    {key === 'planOk' && <PlanOkDetails entry={value as PlanOkCrmEntry} />}
                    {key === 'sci' && <SciDetails entry={value as SciCrmEntry} />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CrmConnectionsSection;
